import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";

function VerificationBlanchisserie() {
  const { numero } = useParams();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const verifier = async () => {
      try {
        const response = await api.get(
          `/public/blanchisserie/${numero}`
        );

        setData(response.data);
      } catch (err) {
        console.error(
          "Erreur vérification blanchisserie:",
          err
        );

        setError(
          err.response?.data?.error ||
          "Ce reçu est introuvable ou invalide."
        );
      } finally {
        setLoading(false);
      }
    };

    verifier();
  }, [numero]);

  const formatMoney = (montant) =>
    Number(montant).toLocaleString("fr-FR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  const formatDate = (date) =>
    new Date(date).toLocaleString("fr-FR", {
      dateStyle: "long",
      timeStyle: "short",
    });

  if (loading) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.loading}>
            Vérification du reçu...
          </div>
        </div>
      </div>
    );
  }

  if (error || !data?.encaissement) {
    return (
      <div style={styles.page}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>✕</div>

          <h1 style={styles.errorTitle}>
            Reçu introuvable
          </h1>

          <p style={styles.errorText}>
            {error ||
              "Ce reçu n'existe pas ou n'est plus disponible."}
          </p>
        </div>
      </div>
    );
  }

  const encaissement = data.encaissement;
  const lignes = data.lignes || [];

  return (
    <div style={styles.page}>

      <div style={styles.card}>

        {/* EN-TÊTE */}

        <div style={styles.header}>

          <div style={styles.logo}>
            ALTA
          </div>

          <div style={styles.company}>
            IMMEUBLE
          </div>

          <div style={styles.service}>
            BLANCHISSERIE
          </div>

        </div>

        {/* STATUT */}

        <div style={styles.verified}>

          <div style={styles.check}>
            ✓
          </div>

          <div>
            <div style={styles.verifiedTitle}>
              Reçu authentique
            </div>

            <div style={styles.verifiedText}>
              Ce reçu a été vérifié avec succès.
            </div>
          </div>

        </div>

        {/* NUMÉRO */}

        <div style={styles.numeroBox}>

          <span style={styles.numeroLabel}>
            NUMÉRO DU REÇU
          </span>

          <strong style={styles.numero}>
            {encaissement.numero}
          </strong>

        </div>

        {/* INFORMATIONS */}

        <div style={styles.infoGrid}>

          <div style={styles.infoItem}>
            <span style={styles.label}>
              Client
            </span>

            <strong>
              {encaissement.client_nom ||
                "Client de passage"}
            </strong>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>
              Date et heure
            </span>

            <strong>
              {formatDate(
                encaissement.date_encaissement
              )}
            </strong>
          </div>

          <div style={styles.infoItem}>
            <span style={styles.label}>
              Mode de paiement
            </span>

            <strong>
              {encaissement.mode_paiement}
            </strong>
          </div>

        </div>

        {/* PRESTATIONS */}

        <div style={styles.section}>

          <h2 style={styles.sectionTitle}>
            Prestations
          </h2>

          <div style={styles.lines}>

            {lignes.map((ligne) => (

              <div
                key={ligne.id || ligne.designation}
                style={styles.line}
              >

                <div style={styles.lineMain}>
                  <strong>
                    {ligne.designation}
                  </strong>

                  <span style={styles.quantity}>
                    {ligne.quantite} ×{" "}
                    {formatMoney(
                      ligne.prix_unitaire
                    )}
                  </span>
                </div>

                <strong>
                  {formatMoney(ligne.total)}{" "}
                  {encaissement.devise}
                </strong>

              </div>

            ))}

          </div>

        </div>

        {/* TOTAL */}

        <div style={styles.totalBox}>

          <span>
            TOTAL ENCAISSÉ
          </span>

          <strong>
            {formatMoney(
              encaissement.montant
            )}{" "}
            {encaissement.devise}
          </strong>

        </div>

        {/* OBSERVATION */}

        {encaissement.observation && (
          <div style={styles.observation}>

            <span style={styles.label}>
              Observation
            </span>

            <p>
              {encaissement.observation}
            </p>

          </div>
        )}

        {/* FOOTER */}

        <div style={styles.footer}>

          <p>
            Ce document constitue une preuve
            d'encaissement de prestation de
            blanchisserie.
          </p>

          <small>
            Vérification publique — Immalta
          </small>

        </div>

      </div>

    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "#f4f6f8",
    padding: "30px 16px",
    boxSizing: "border-box",
    fontFamily:
      "Arial, Helvetica, sans-serif",
  },

  card: {
    width: "100%",
    maxWidth: "700px",
    margin: "0 auto",
    background: "#fff",
    borderRadius: "16px",
    padding: "32px",
    boxSizing: "border-box",
    boxShadow:
      "0 4px 20px rgba(0,0,0,0.08)",
  },

  header: {
    textAlign: "center",
    marginBottom: "24px",
  },

  logo: {
    fontSize: "28px",
    fontWeight: "800",
    letterSpacing: "3px",
  },

  company: {
    fontSize: "13px",
    letterSpacing: "4px",
    marginTop: "2px",
  },

  service: {
    marginTop: "14px",
    fontSize: "18px",
    fontWeight: "700",
  },

  verified: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "16px",
    borderRadius: "12px",
    background: "#f0fdf4",
    border: "1px solid #bbf7d0",
    marginBottom: "20px",
  },

  check: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#16a34a",
    color: "#fff",
    fontSize: "22px",
    fontWeight: "700",
    flexShrink: 0,
  },

  verifiedTitle: {
    fontWeight: "700",
    color: "#166534",
    fontSize: "16px",
  },

  verifiedText: {
    marginTop: "3px",
    color: "#15803d",
    fontSize: "13px",
  },

  numeroBox: {
    textAlign: "center",
    padding: "18px",
    borderRadius: "10px",
    background: "#f8fafc",
    marginBottom: "22px",
  },

  numeroLabel: {
    display: "block",
    fontSize: "11px",
    color: "#64748b",
    letterSpacing: "1px",
    marginBottom: "6px",
  },

  numero: {
    fontSize: "22px",
    letterSpacing: "1px",
  },

  infoGrid: {
    display: "grid",
    gridTemplateColumns:
      "repeat(auto-fit, minmax(180px, 1fr))",
    gap: "18px",
    marginBottom: "26px",
  },

  infoItem: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
    fontSize: "14px",
  },

  label: {
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: "#64748b",
  },

  section: {
    marginTop: "10px",
  },

  sectionTitle: {
    fontSize: "16px",
    marginBottom: "12px",
  },

  lines: {
    borderTop: "1px solid #e5e7eb",
  },

  line: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "20px",
    padding: "13px 0",
    borderBottom: "1px solid #f1f5f9",
    fontSize: "14px",
  },

  lineMain: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  quantity: {
    color: "#64748b",
    fontSize: "12px",
  },

  totalBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: "20px",
    padding: "18px",
    borderRadius: "10px",
    background: "#f8fafc",
    fontSize: "18px",
  },

  observation: {
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #e5e7eb",
    fontSize: "13px",
  },

  footer: {
    textAlign: "center",
    marginTop: "30px",
    paddingTop: "20px",
    borderTop: "1px dashed #d1d5db",
    color: "#64748b",
    fontSize: "12px",
    lineHeight: "1.6",
  },

  loading: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#64748b",
  },

  errorIcon: {
    width: "48px",
    height: "48px",
    margin: "20px auto",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fee2e2",
    color: "#b91c1c",
    fontSize: "24px",
    fontWeight: "700",
  },

  errorTitle: {
    textAlign: "center",
    margin: "10px 0",
  },

  errorText: {
    textAlign: "center",
    color: "#64748b",
  },
};

export default VerificationBlanchisserie;